/**
 * Document Type Configuration
 *
 * This module defines the available document types that can be generated
 * via the AI chat interface. Each document type has configurable display
 * properties and can be extended to add new document types.
 *
 * To add a new document type:
 * 1. Add a new entry to DOCUMENT_TYPE_CONFIGS
 * 2. Ensure the type_key matches the backend (chat.py DOCUMENT_TYPE_CONFIGS)
 * 3. The frontend will automatically handle the new type
 */

export interface DocumentTypeConfig {
  /** Internal identifier used in code and database */
  typeKey: string;
  /** Text shown on the pill/button in the chat */
  displayName: string;
  /** Summary message shown in chat after document generation */
  summaryMessage: string;
}

/**
 * Configuration for all document types
 * Keys must match the backend type_key values
 */
export const DOCUMENT_TYPE_CONFIGS: Record<string, DocumentTypeConfig> = {
  hpi_document: {
    typeKey: 'hpi_document',
    displayName: 'RO-Consult',
    summaryMessage:
      "I've generated a draft RO-Consult document. " +
      'The document includes relevant patient history, symptoms, and clinical findings. ' +
      'You can edit or expand any section as needed.',
  },
  prostate_mri_referral: {
    typeKey: 'prostate_mri_referral',
    displayName: 'DRAFTED MRI REFERRAL',
    summaryMessage: "I've drafted the MRI referral request. " + 'You can review and edit the details as needed.',
  },
};

/**
 * Get the configuration for a document type
 * @param typeKey - The document type key
 * @returns The document type configuration, or undefined if not found
 */
export const getDocumentTypeConfig = (typeKey: string): DocumentTypeConfig | undefined => {
  return DOCUMENT_TYPE_CONFIGS[typeKey];
};

/**
 * Check if a type is a document type (has a canvas/editor)
 * @param typeKey - The type key to check
 * @returns True if this type should open a document canvas
 */
export const isDocumentType = (typeKey: string): boolean => {
  return typeKey in DOCUMENT_TYPE_CONFIGS;
};

/**
 * Get the display name for a document type
 * @param typeKey - The document type key
 * @returns The display name, or a default if not found
 */
export const getDocumentDisplayName = (typeKey: string): string => {
  const config = getDocumentTypeConfig(typeKey);
  return config?.displayName ?? 'DOCUMENT';
};

/**
 * Get the summary message for a document type
 * @param typeKey - The document type key
 * @returns The summary message, or a default if not found
 */
export const getDocumentSummaryMessage = (typeKey: string): string => {
  const config = getDocumentTypeConfig(typeKey);
  return config?.summaryMessage ?? 'Document generated successfully.';
};
